import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import type * as React from 'react';

const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-lg border px-2 py-1.5 text-left text-xs/relaxed has-data-[slot=alert-action]:pr-30 has-data-[slot=alert-dismiss]:pr-8 has-data-[slot=alert-action]:has-data-[slot=alert-dismiss]:pr-38 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-1.5 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-3.5",
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        info: 'border-info/30 bg-info-muted text-info *:data-[slot=alert-description]:text-info/90',
        success: 'border-success/30 bg-success-muted text-success *:data-[slot=alert-description]:text-success/90',
        warning: 'border-warning/30 bg-warning-muted text-warning *:data-[slot=alert-description]:text-warning/90',
        destructive:
          'border-destructive/30 bg-destructive-muted text-destructive *:data-[slot=alert-description]:text-destructive/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

// gloss
type AlertProps = React.ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    dismissible?: boolean;
    onDismiss?: () => void;
  };

const Alert = ({ className, variant, dismissible = false, onDismiss, children, ...props }: AlertProps) => {
  return (
    <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {children}
      {dismissible && (
        <button
          type="button"
          data-slot="alert-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-1.5 right-2 cursor-pointer rounded-sm text-current opacity-70 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2 focus-visible:outline-hidden"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
};

const AlertTitle = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
};

const AlertDescription = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-balance text-muted-foreground text-xs/relaxed md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className,
      )}
      {...props}
    />
  );
};

// gloss
const AlertAction = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="alert-action"
      className={cn('absolute top-1.5 right-2 group-has-data-[slot=alert-dismiss]/alert:right-9', className)}
      {...props}
    />
  );
};

export type { AlertProps };
export { Alert, AlertAction, AlertDescription, AlertTitle, alertVariants };
